import {
	CustomerScreen,
	CustomerSkeleton,
	customerQuery,
} from "@commerce/customers/CustomerScreen";
import { isOrganizationAdmin } from "@repo/auth/lib/helper";
import { config } from "@repo/config";
import { AsyncBoundary } from "@shared/components/AsyncBoundary";
import { dehydrateForLoader, getServerQueryClient } from "@shared/lib/server";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute(
	"/_saas/app/_org/$organizationSlug/customers/$customerId",
)({
	beforeLoad: ({ context }) => ({
		isOrganizationAdmin: isOrganizationAdmin(
			context.organization,
			context.session.user,
		),
	}),
	loader: async ({ context, params }) => {
		const organizationId = context.organization.id;
		const queryClient = getServerQueryClient();
		const customer = await queryClient
			.ensureQueryData(customerQuery(organizationId, params.customerId))
			.catch(() => null);
		return {
			organizationId,
			name: customer?.name ?? customer?.profileName ?? null,
			dehydratedState: dehydrateForLoader(queryClient),
		};
	},
	// Shown inside the Shell while the loader runs on a client navigation.
	pendingComponent: CustomerSkeleton,
	head: ({ loaderData }) => ({
		meta: [
			{ title: `${loaderData?.name ?? "Customer"} - ${config.appName}` },
		],
	}),
	component: CustomerPage,
});

function CustomerPage() {
	const { customerId } = Route.useParams();
	const { isOrganizationAdmin } = Route.useRouteContext();
	const { organizationId, dehydratedState } = Route.useLoaderData();
	return (
		<AsyncBoundary
			fallback={<CustomerSkeleton />}
			dehydratedState={dehydratedState}
			errorFallback="inline"
			resetKeys={[customerId]}
		>
			<CustomerScreen
				organizationId={organizationId}
				customerId={customerId}
				isAdmin={isOrganizationAdmin}
			/>
		</AsyncBoundary>
	);
}
